import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { EXPORT_TIMESERIES_PROGRESS_LOGIC_EC } from '../../constants';

const LinearProgressWithLabel = (properties) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Typography
        variant="body2"
        color="inherit"
      >{`${Math.round(properties.value)}%`}</Typography>
    </Box>
    <Box sx={{ width: '100%', mr: 1 }}>
      <LinearProgress
        variant="determinate"
        color="inherit"
        {...properties}
      />
    </Box>
  </Box>
);
LinearProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate and buffer variants.
   * Value between 0 and 100.
   */
  value: PropTypes.number.isRequired,
};

const ExportProgress = (properties) => {
  const {
    exportType = 'signal',
    retrievedDataPoints,
    expectedDataPoints,
    onExportCompleted,
    useStaticTimer,
    increment: currentRequest,
    variables = [],
  } = properties;
  const [progress, setProgress] = useState(0);

  if (exportType === 'event') {
    const numRequests = Math.ceil(expectedDataPoints / 10_000);
    const currentProgress = (currentRequest / numRequests) * 100 || 0;
    if (currentProgress !== progress) {
      setProgress(currentProgress);
    }
  }

  const getProgressParameters = () => {
    // default: signal export
    const incrementKeys = Object.keys(EXPORT_TIMESERIES_PROGRESS_LOGIC_EC);
    const dataPointRange = incrementKeys.reduce(
      (previous, current) =>
        Math.abs(current - expectedDataPoints) < Math.abs(previous - expectedDataPoints)
          ? current
          : previous,
      incrementKeys[0]
    );
    const progressParameters = EXPORT_TIMESERIES_PROGRESS_LOGIC_EC[dataPointRange];
    const progressParametersForDataPoints = Object.keys(progressParameters);
    const noOfSignals = variables?.length;
    if (!noOfSignals) {
      return progressParameters.default;
    }
    const signalRange = progressParametersForDataPoints.reduce(
      (previous, current) =>
        Math.abs(current - noOfSignals) < Math.abs(previous - noOfSignals) ? current : previous,
      progressParametersForDataPoints[0]
    );
    return progressParameters[signalRange] || progressParameters.default;
  };

  const [increment, time] = getProgressParameters();

  useEffect(() => {
    if (exportType === 'event' && progress === 100 && retrievedDataPoints === expectedDataPoints) {
      onExportCompleted();
    }

    if (exportType === 'signal' && progress === 100) {
      onExportCompleted();
    }
  }, [progress, exportType, retrievedDataPoints, expectedDataPoints, onExportCompleted]);

  useEffect(() => {
    let intervalId;
    if (exportType === 'signal') {
      intervalId = setInterval(
        () => {
          setProgress((previousProgress) => {
            if (previousProgress + increment >= 100) {
              return retrievedDataPoints > 0 ? 100 : previousProgress;
            }
            return previousProgress + increment;
          });
        },
        retrievedDataPoints > 0 ? 100 : (useStaticTimer ?? time)
      );
      if (progress === 100) {
        clearInterval(intervalId);
      }
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportType, progress, retrievedDataPoints, increment, time, useStaticTimer]);

  return (
    <Box sx={{ width: '100%', paddingBottom: '1rem' }}>
      <div> Downloading ... </div>
      <LinearProgressWithLabel value={progress} />
    </Box>
  );
};

ExportProgress.propTypes = {
  exportType: PropTypes.string,
  retrievedDataPoints: PropTypes.number.isRequired,
  expectedDataPoints: PropTypes.number.isRequired,
  onExportCompleted: PropTypes.func.isRequired,
  useStaticTimer: PropTypes.number,
  increment: PropTypes.number.isRequired,
  variables: PropTypes.arrayOf(PropTypes.object),
};

export default ExportProgress;
